import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Reorder } from 'framer-motion';
import { OptionsListContainer } from '../Styles';
import { DraggableOption } from './DraggableOption';
import { Option } from './types';

interface OptionsListProps {
  selectedOptions: Option[];
  setSelectedOptions: (options: Option[]) => void;
  handleEditOption: (option: Option, isSoldOut?: boolean) => void;
  handleOptionRemove: (optionId: number | string) => void;
  optionsBorderColor: string;
  selectedItem: number | null;
  soldOutLoading?: number | string | null;
  onOrderChange?: (draggedOption: Option, fromIndex: number, toIndex: number, displacedOption?: Option) => void;
}

export const OptionsList: React.FC<OptionsListProps> = ({
  selectedOptions,
  setSelectedOptions,
  handleEditOption,
  handleOptionRemove,
  optionsBorderColor,
  selectedItem,
  soldOutLoading,
  onOrderChange,
}) => {
  const [optionOrder, setOptionOrder] = useState<(number | string)[]>([]);
  const initialOrderRef = useRef<(number | string)[]>([]);
  const isDraggingRef = useRef<boolean>(false);
  const onOrderChangeRef = useRef(onOrderChange);
  const selectedOptionsRef = useRef(selectedOptions);

  useEffect(() => {
    onOrderChangeRef.current = onOrderChange;
    selectedOptionsRef.current = selectedOptions;
  }, [onOrderChange, selectedOptions]);

  useEffect(() => {
    const sortedOptions = [...selectedOptions]
      .filter((option) => option?.id)
      .sort((a, b) => (a.sequence_number || 0) - (b.sequence_number || 0));
    
    const newOrder = sortedOptions.map((opt) => opt.id);
    setOptionOrder(newOrder);
    initialOrderRef.current = newOrder;
  }, [selectedOptions]);

  const handleDragEnd = useCallback(async () => {
    const finalOrder = [...optionOrder];
    const initialOrder = initialOrderRef.current;

    if (initialOrder.length === 0 || finalOrder.length === 0) {
      return;
    }

    const orderChanged = initialOrder.some((id, index) => finalOrder[index] !== id);
    const onOrderChangeCallback = onOrderChangeRef.current;
    
    if (!orderChanged || !onOrderChangeCallback) {
      return;
    }

    try {
      let draggedId: number | string | null = null;
      let draggedFromIndex = -1;
      let draggedToIndex = -1;

      for (let i = 0; i < initialOrder.length; i++) {
        const id = initialOrder[i];
        const newIndex = finalOrder.findIndex((finalId) => finalId === id);
        if (newIndex !== i) {
          draggedId = id;
          draggedFromIndex = i;
          draggedToIndex = newIndex;
          break;
        }
      }

      if (draggedId === null || draggedFromIndex === -1 || draggedToIndex === -1) {
        return;
      }

      const currentSelectedOptions = selectedOptionsRef.current;
      const draggedOption = currentSelectedOptions.find((opt) => opt.id === draggedId);
      if (!draggedOption) {
        return;
      }

      // Get the displaced option (the one that was at the target position initially)
      const displacedId = initialOrder[draggedToIndex];
      const displacedOption = currentSelectedOptions.find((opt) => opt.id === displacedId);

      // Call API with indices (not sequence_number) - API will use these indices
      if (displacedOption && displacedOption.id !== draggedOption.id) {
        await onOrderChangeCallback(
          draggedOption,
          draggedFromIndex,
          draggedToIndex,
          displacedOption
        );
      } else {
        await onOrderChangeCallback(
          draggedOption,
          draggedFromIndex,
          draggedToIndex,
          undefined
        );
      }
    } catch (error) {
      console.error('Error updating sequence numbers:', error);
    }
  }, [optionOrder]);

  const handleDragEndRef = useRef(handleDragEnd);
  useEffect(() => {
    handleDragEndRef.current = handleDragEnd;
  }, [handleDragEnd]);

  useEffect(() => {
    const handleMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        handleDragEndRef.current();
        initialOrderRef.current = [];
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('pointerup', handleMouseUp);
    document.addEventListener('touchend', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('pointerup', handleMouseUp);
      document.removeEventListener('touchend', handleMouseUp);
    };
  }, []);
  
  const handleReorder = useCallback((newOrder: (number | string)[]) => {
    if (!isDraggingRef.current) {
      isDraggingRef.current = true;
      initialOrderRef.current = [...optionOrder];
    }
    
    setOptionOrder(newOrder);
  }, [optionOrder]);

  const orderedOptions = useMemo(() => {
    return optionOrder
      .map((id) => selectedOptions.find((opt) => opt.id === id))
      .filter(Boolean) as Option[];
  }, [optionOrder, selectedOptions]);

  return (
    <OptionsListContainer>
      <Reorder.Group
        axis="y"
        values={optionOrder}
        onReorder={handleReorder}
        style={{ listStyle: 'none', padding: 0, margin: 0 }}
      >
        {orderedOptions.map((option) => (
          <DraggableOption
            key={option.id}
            option={option}
            value={option.id}
            handleEditOption={handleEditOption}
            handleOptionRemove={handleOptionRemove}
            optionsBorderColor={optionsBorderColor}
            soldOutLoading={soldOutLoading}
          />
        ))}
      </Reorder.Group>
    </OptionsListContainer>
  );
};
